import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import productService from '../services/productService';
import { resolveImageUrl, sanitizeImages } from '../utils/asset';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await productService.getProduct(id);
        if (!mounted) return;
        // Sanitize images client-side
        data.images = sanitizeImages(data.images);
        setProduct(data);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || err.message || 'Failed to load product');
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="page-wrapper"><p>Loading product...</p></div>;
  if (error) return <div className="page-wrapper"><p className="error-inline">{error}</p><button className="btn" onClick={() => navigate(-1)}>Back</button></div>;
  if (!product) return null;

  const images = product.images || [];
  const mainImage = images[activeIdx];
  const attrEntries = product.attributes ? Object.entries(product.attributes) : [];
  const keywordList = Array.isArray(product.searchKeywords) ? product.searchKeywords : [];
  const hasShipping = product.shipping && (product.shipping.incoterm || product.shipping.origin?.country || (Array.isArray(product.shipping.methods) && product.shipping.methods.length));

  return (
    <div className="product-detail page-wrapper">
      <div className="page-header">
        <h1>{product.title}</h1>
        <button className="btn text" onClick={() => navigate('/products')}>Back to Products</button>
      </div>
      <div className="detail-layout">
        <div className="media-column">
          {mainImage ? (
            <figure className="main-image">
              <img src={resolveImageUrl(mainImage.url)} alt={mainImage.alt || product.title} />
            </figure>
          ) : (
            <div className="image-placeholder">No image</div>
          )}
          {images.length > 1 && (
            <div className="thumbs" role="list">
              {images.map((img, idx) => (
                <button
                  key={img.url + idx}
                  className={idx === activeIdx ? 'thumb active' : 'thumb'}
                  onClick={() => setActiveIdx(idx)}
                  aria-label={`Show image ${idx + 1}`}
                >
                  <img src={resolveImageUrl(img.url)} alt={img.alt || `Image ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="info-column panel">
          <h2>Details</h2>
          {product.description && <p className="description">{product.description}</p>}
          <div className="meta-grid">
            <div><strong>Base Price:</strong> {product.price?.base != null ? `$${product.price.base.toFixed(2)}` : '—'}</div>
            <div><strong>Available:</strong> {product.inventory?.available ?? 0}</div>
            {product.moq != null && <div><strong>MOQ:</strong> {product.moq}</div>}
            {product.packSize != null && <div><strong>Pack Size:</strong> {product.packSize}</div>}
            {product.inventory?.leadTimeDays != null && <div><strong>Lead Time (days):</strong> {product.inventory.leadTimeDays}</div>}
            {product.modelNumber && <div><strong>Model #:</strong> {product.modelNumber}</div>}
            {product.sku && <div><strong>SKU:</strong> {product.sku}</div>}
            {Array.isArray(product.categories) && product.categories.length > 0 && (
              <div className="full"><strong>Categories:</strong> {product.categories.join(', ')}</div>
            )}
            {Array.isArray(product.price?.tiers) && product.price.tiers.length > 0 && (
              <div className="full tiers"><strong>Tier Pricing:</strong>
                <ul>
                  {product.price.tiers.map((t,i) => (
                    <li key={i}>≥ {t.minQty}: ${t.unitPrice}</li>
                  ))}
                </ul>
              </div>
            )}
            {hasShipping && (
              <div className="full shipping-block">
                <strong>Shipping:</strong>
                <ul className="plain-list">
                  {product.shipping.incoterm && <li>Incoterm: {product.shipping.incoterm}</li>}
                  {product.shipping.origin?.country && <li>Origin: {product.shipping.origin.country}{product.shipping.origin.state ? ', ' + product.shipping.origin.state : ''}</li>}
                  {Array.isArray(product.shipping.methods) && product.shipping.methods.length > 0 && <li>Methods: {product.shipping.methods.join(', ')}</li>}
                </ul>
              </div>
            )}
            {attrEntries.length > 0 && (
              <div className="full attrs-block">
                <strong>Attributes:</strong>
                <ul className="plain-list">
                  {attrEntries.map(([k,v]) => (
                    <li key={k}><span className="attr-key">{k}:</span> <span className="attr-val">{String(v)}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {keywordList.length > 0 && (
              <div className="full"><strong>Keywords:</strong> {keywordList.join(', ')}</div>
            )}
            <div className="full status-line">
              <strong>Status:</strong> {product.isActive ? 'Active' : 'Inactive'}{product.moderationHidden ? ' (Hidden by Moderation)' : ''}
            </div>
            <div className="full meta-timestamps">
              <small>Created: {new Date(product.createdAt).toLocaleString()} | Updated: {new Date(product.updatedAt).toLocaleString()}</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
